<script setup lang="ts">
  import {
    computed,
    getCurrentInstance,
    nextTick,
    onBeforeUnmount,
    ref,
    watch,
  } from 'vue';
  import { usePreferredReducedMotion } from '@vueuse/core';
  import PosUiChip from '@/components/pos-v2/ui/PosUiChip.vue';
  import PosUiEmptyState from '@/components/pos-v2/ui/PosUiEmptyState.vue';
  import PosUiIcon from '@/components/pos-v2/ui/PosUiIcon.vue';
  import {
    VIEWER_ZOOM_MAX,
    VIEWER_ZOOM_MIN,
    itemSourcePageId,
    pageOverlays,
    resolveViewerPage,
    viewerNumbers,
    viewerPages,
    zoomIn,
    zoomOut,
  } from '@/domain/menu-import/source-viewer';
  import type {
    IBoxRect,
    IViewerOverlay,
  } from '@/domain/menu-import/source-viewer';
  import type { IMenuDraft, IMenuImportPage } from '@/interfaces/IMenuImport';
  import { Globals } from '@/modules/Globals';

  /**
   * «Carta original» (plan §4.5.5): the photos of the menu, one page at a
   * time (page chips), in a zoom box («−» / «+», 1× to 3×). Every dish read
   * from the page has a box over it: amber when it needs a look, orange for
   * the dish selected. Tapping a box selects that dish in the list; nothing
   * here changes the menu.
   */
  // Nullable props are optional with a null default: Vue 2 would check a
  // required `null` against a `null` constructor.
  const props = withDefaults(
    defineProps<{
      pages: IMenuImportPage[];
      draft?: IMenuDraft | null;
      selectedItemId?: string | null;
      /** A page asked for from outside («Ver en la carta»). */
      focusPageId?: string | null;
      boxes: boolean;
    }>(),
    { draft: null, selectedItemId: null, focusPageId: null }
  );
  const emit = defineEmits<{
    (e: 'select-item', itemId: string): void;
    /** The owner picked another page. */
    (e: 'page', pageId: string): void;
  }>();
  const proxy = getCurrentInstance()!.proxy;
  const tr = (key: string, params?: Record<string, unknown>) =>
    String(proxy.$t(key, params));
  const reducedMotion = usePreferredReducedMotion();

  const stage = ref<HTMLElement | null>(null);
  const zoom = ref(VIEWER_ZOOM_MIN);
  /** Pages whose image did not load. */
  const failedPages = ref<ReadonlySet<string>>(new Set());
  let alive = true;

  const shownPages = computed(() => viewerPages(props.pages));
  /** The number of each shown image among the pages the owner added. */
  const numbers = computed(() => viewerNumbers(shownPages.value));
  const pageId = ref<string | null>(
    resolveViewerPage(shownPages.value, null, props.focusPageId)
  );
  const pageIndex = computed(() =>
    shownPages.value.findIndex((page) => page.pageId === pageId.value)
  );
  const page = computed(() => shownPages.value[pageIndex.value] ?? null);
  const pageLabel = computed(() =>
    page.value ? chipLabel(pageIndex.value) : ''
  );
  const imageUrl = computed(() =>
    page.value && page.value.storageKey !== null
      ? `${Globals.uploadsPath}/${page.value.storageKey}`
      : null
  );
  const imageFailed = computed(
    () => !!page.value && failedPages.value.has(page.value.pageId)
  );
  const overlays = computed<IViewerOverlay[]>(() =>
    props.draft && page.value
      ? pageOverlays(props.draft, page.value.pageId, props.boxes)
      : []
  );

  /** «Página 2 de 3», or «Página 2 de 3, parte 4» for a part of a very tall page. */
  function chipLabel(index: number): string {
    const number = numbers.value[index];
    if (!number) return '';
    return number.part === null
      ? tr('menuImport.viewer.page', {
          page: number.page,
          total: number.total,
        })
      : tr('menuImport.viewer.pagePart', {
          page: number.page,
          total: number.total,
          part: number.part,
        });
  }

  /** «2», or «2.4» for the fourth part of the second page. */
  function chipText(index: number): string {
    const number = numbers.value[index];
    if (!number) return '';
    return number.part === null
      ? String(number.page)
      : `${number.page}.${number.part}`;
  }

  function boxStyle(rect: IBoxRect): Record<string, string> {
    return {
      top: `${rect.top}%`,
      left: `${rect.left}%`,
      width: `${rect.width}%`,
      height: `${rect.height}%`,
    };
  }

  function showPage(next: string | null) {
    if (next === pageId.value) return;
    pageId.value = next;
    zoom.value = VIEWER_ZOOM_MIN;
    if (stage.value) stage.value.scrollTop = stage.value.scrollLeft = 0;
  }

  function pickPage(next: IMenuImportPage) {
    showPage(next.pageId);
    emit('page', next.pageId);
  }

  function onImageError() {
    const current = page.value;
    if (current)
      failedPages.value = new Set([...failedPages.value, current.pageId]);
  }

  /** Centres the selected dish's box in the zoom box (only this box scrolls). */
  async function showSelectedBox() {
    await nextTick();
    const container = stage.value;
    const itemId = props.selectedItemId;
    if (!alive || !container || itemId === null) return;
    const box = Array.from(
      container.querySelectorAll('[data-box-item-id]')
    ).find((element) => element.getAttribute('data-box-item-id') === itemId);
    if (!(box instanceof HTMLElement)) return;
    container.scrollTo({
      top: box.offsetTop - (container.clientHeight - box.offsetHeight) / 2,
      left: box.offsetLeft - (container.clientWidth - box.offsetWidth) / 2,
      behavior: reducedMotion.value === 'reduce' ? 'auto' : 'smooth',
    });
  }

  function changeZoom(next: number) {
    const container = stage.value;
    if (!container || next === zoom.value) {
      zoom.value = next;
      return;
    }
    // Keep the middle of what the owner looks at in the middle.
    const ratio = next / zoom.value;
    const middleX = container.scrollLeft + container.clientWidth / 2;
    const middleY = container.scrollTop + container.clientHeight / 2;
    zoom.value = next;
    void nextTick(() => {
      if (!alive) return;
      container.scrollLeft = middleX * ratio - container.clientWidth / 2;
      container.scrollTop = middleY * ratio - container.clientHeight / 2;
    });
  }

  // «Ver en la carta» and other requests from outside.
  watch(
    () => props.focusPageId,
    (requested) => {
      if (requested === null) return;
      showPage(resolveViewerPage(shownPages.value, pageId.value, requested));
      void showSelectedBox();
    }
  );

  // A dish selected elsewhere: its page, with its box in view.
  watch(
    () => props.selectedItemId,
    (itemId) => {
      const item = itemId !== null ? props.draft?.items[itemId] : undefined;
      if (!item) return;
      const onThisPage = item.sourceRefs.some(
        (ref) => ref.pageId === pageId.value
      );
      if (!onThisPage) {
        const target = itemSourcePageId(item, props.pages);
        if (target !== null) showPage(target);
      }
      void showSelectedBox();
    }
  );

  // Pages added or removed: stay on the page shown while it exists.
  watch(shownPages, (pages) => {
    showPage(resolveViewerPage(pages, pageId.value, null));
  });

  onBeforeUnmount(() => {
    alive = false;
  });
</script>

<template>
  <div class="v2-menu-source-viewer">
    <PosUiEmptyState
      v-if="shownPages.length === 0"
      class="viewer-empty"
      icon="image-off-outline"
      :title="$t('menuImport.viewer.noPages')"
    />
    <template v-else>
      <div v-if="shownPages.length > 1" class="viewer-pages">
        <PosUiChip
          v-for="(item, index) in shownPages"
          :key="item.pageId"
          class="page-chip"
          :active="item.pageId === pageId"
          :aria-label="chipLabel(index)"
          @click="pickPage(item)"
          >{{ chipText(index) }}</PosUiChip
        >
      </div>
      <div ref="stage" class="viewer-stage">
        <div class="stage-canvas" :style="{ width: `${zoom * 100}%` }">
          <img
            v-if="imageUrl && !imageFailed"
            class="stage-image"
            :src="imageUrl"
            :alt="pageLabel"
            draggable="false"
            @load="showSelectedBox"
            @error="onImageError"
          />
          <div v-else class="stage-missing" aria-hidden="true">
            <PosUiIcon name="image-off-outline" :size="32" />
          </div>
          <template v-if="imageUrl && !imageFailed">
            <button
              v-for="overlay in overlays"
              :key="overlay.itemId"
              type="button"
              class="stage-box"
              :class="[
                `level-${overlay.level}`,
                { 'is-selected': overlay.itemId === selectedItemId },
              ]"
              :style="boxStyle(overlay.rect)"
              :data-box-item-id="overlay.itemId"
              :aria-label="overlay.name"
              :aria-pressed="
                overlay.itemId === selectedItemId ? 'true' : 'false'
              "
              @click="emit('select-item', overlay.itemId)"
            />
          </template>
        </div>
      </div>
      <div class="viewer-foot">
        <p class="viewer-caption">
          <span class="caption-page">{{ pageLabel }}</span>
          <span v-if="overlays.length > 0">
            · {{ $t('menuImport.viewer.hint') }}</span
          >
        </p>
        <div class="viewer-zoom">
          <button
            type="button"
            class="zoom-button"
            :disabled="zoom <= VIEWER_ZOOM_MIN"
            :aria-label="$t('menuImport.viewer.zoomOut')"
            :title="$t('menuImport.viewer.zoomOut')"
            @click="changeZoom(zoomOut(zoom))"
          >
            <PosUiIcon name="minus" :size="20" />
          </button>
          <button
            type="button"
            class="zoom-button"
            :disabled="zoom >= VIEWER_ZOOM_MAX"
            :aria-label="$t('menuImport.viewer.zoomIn')"
            :title="$t('menuImport.viewer.zoomIn')"
            @click="changeZoom(zoomIn(zoom))"
          >
            <PosUiIcon name="plus" :size="20" />
          </button>
        </div>
      </div>
    </template>
  </div>
</template>

<style scoped lang="scss">
  .v2-menu-source-viewer {
    @apply tw-flex tw-min-h-0 tw-flex-1 tw-flex-col;

    .viewer-empty {
      @apply tw-flex-1;
    }
    .viewer-pages {
      @apply tw-flex tw-shrink-0 tw-gap-2 tw-overflow-x-auto tw-px-4 tw-pb-1 tw-pt-3;
      scrollbar-width: none;
      &::-webkit-scrollbar {
        display: none;
      }
      .page-chip {
        @apply tw-min-w-11 tw-tabular-nums;
      }
    }
    // The zoom box: the page fits its width at 1×, and scrolls both ways beyond.
    .viewer-stage {
      @apply tw-relative tw-m-4 tw-mb-0 tw-min-h-0 tw-flex-1 tw-overflow-auto tw-rounded-xl tw-border tw-border-solid tw-border-neutral-200 tw-bg-canvas;
      overscroll-behavior: contain;
      touch-action: pan-x pan-y pinch-zoom;
    }
    .stage-canvas {
      @apply tw-relative tw-min-w-full;
    }
    .stage-image {
      @apply tw-block tw-h-auto tw-w-full tw-select-none;
    }
    .stage-missing {
      @apply tw-flex tw-h-64 tw-items-center tw-justify-center tw-text-neutral-400;
    }
    .stage-box {
      @apply tw-absolute tw-m-0 tw-cursor-pointer tw-rounded tw-border-2 tw-border-solid tw-border-transparent tw-bg-transparent tw-p-0 tw-transition-colors;
      &:hover {
        @apply tw-border-neutral-400;
      }
      &:focus-visible {
        @apply tw-outline-none tw-ring-2 tw-ring-orange-500/40;
      }
      &.level-check,
      &.level-fix {
        @apply tw-border-amber-500 tw-bg-amber-500/10;
      }
      // Active-state indicator: the dish selected.
      &.is-selected {
        @apply tw-z-[1] tw-border-orange-600 tw-bg-orange-600/15;
      }
    }
    .viewer-foot {
      @apply tw-flex tw-shrink-0 tw-items-center tw-gap-3 tw-px-4 tw-py-2;
    }
    .viewer-caption {
      @apply tw-m-0 tw-min-w-0 tw-flex-1 tw-text-xs tw-text-neutral-500;
      .caption-page {
        @apply tw-font-medium tw-text-neutral-700;
      }
    }
    .viewer-zoom {
      @apply tw-flex tw-shrink-0 tw-gap-1;
    }
    .zoom-button {
      @apply tw-flex tw-h-11 tw-w-11 tw-cursor-pointer tw-items-center tw-justify-center tw-rounded-lg tw-border tw-border-solid tw-border-neutral-300 tw-bg-white tw-text-neutral-700 hover:tw-bg-neutral-50;
      &:focus-visible {
        @apply tw-outline-none tw-ring-2 tw-ring-orange-500/40;
      }
      &:disabled {
        @apply tw-cursor-not-allowed tw-border-neutral-200 tw-bg-neutral-100 tw-text-neutral-400 hover:tw-bg-neutral-100;
      }
    }
  }
</style>
